import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import db from '../../utils/database';
import ConfirmDialog from '../common/ConfirmDialog';

/**
 * TableCreator - A component for creating and managing database tables
 * 
 * @param {Object} props
 * @param {function} props.onTableCreated - Callback when a table is created
 * @param {function} props.onRefreshTables - Callback to refresh tables list
 * @param {Array} props.existingTables - List of existing tables to prevent duplicates
 */
const TableCreator = ({ 
  onTableCreated, 
  onRefreshTables,
  existingTables = [] 
}) => {
  const notification = useNotification();
  
  const [newTableName, setNewTableName] = useState('');
  const [columns, setColumns] = useState([
    { id: 'col-1', name: 'id', type: 'INTEGER', primaryKey: true, autoIncrement: true, notNull: true, default: '', unique: false }
  ]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' or 'delete'
  const [tableToDelete, setTableToDelete] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState({
    tableName: true,
    columns: {}
  });
  const [previewSql, setPreviewSql] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Available SQL data types
  const dataTypes = [
    { value: 'INTEGER', label: 'INTEGER' },
    { value: 'TEXT', label: 'TEXT' },
    { value: 'REAL', label: 'REAL (Float)' },
    { value: 'BLOB', label: 'BLOB (Binary)' },
    { value: 'NUMERIC', label: 'NUMERIC' },
    { value: 'BOOLEAN', label: 'BOOLEAN' },
    { value: 'DATE', label: 'DATE' },
    { value: 'DATETIME', label: 'DATETIME' },
    { value: 'TIMESTAMP', label: 'TIMESTAMP' }
  ];
  
  // Generate a unique column ID
  const generateColumnId = () => {
    return `col-${Date.now()}`;
  };
  
  // Add a new column to the table definition
  const addColumn = () => {
    const newColumnId = generateColumnId();
    setColumns([
      ...columns,
      { id: newColumnId, name: '', type: 'TEXT', primaryKey: false, autoIncrement: false, notNull: false, default: '', unique: false }
    ]);
    
    // Add validation state for new column
    setValidation(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [newColumnId]: { name: true }
      }
    }));
  };
  
  // Remove a column from the table definition
  const removeColumn = (columnId) => {
    if (columns.length <= 1) {
      notification.showWarning('Tables must have at least one column');
      return;
    }
    
    const updatedColumns = columns.filter(col => col.id !== columnId);
    setColumns(updatedColumns);
    
    // Update validation state
    setValidation(prev => {
      const { [columnId]: _, ...remaining } = prev.columns;
      return {
        ...prev,
        columns: remaining
      };
    });
  };
  
  // Update a column definition
  const updateColumn = (columnId, field, value) => {
    const updatedColumns = columns.map(column => {
      if (column.id === columnId) {
        const updatedColumn = { ...column, [field]: value };
        
        // Handle auto-increment: only available on INTEGER primary keys
        if (field === 'type' && value !== 'INTEGER') {
          updatedColumn.autoIncrement = false;
        }
        
        // Handle primary key changes
        if (field === 'primaryKey') {
          // When adding a primary key, remove primary key from other columns
          if (value === true) {
            // Make it NOT NULL automatically
            updatedColumn.notNull = true;
            
            // Unset primary key on other columns
            columns.forEach(col => {
              if (col.id !== columnId && col.primaryKey) {
                col.primaryKey = false;
                col.autoIncrement = false;
              }
            });
          } else {
            // When removing primary key, clear auto-increment
            updatedColumn.autoIncrement = false;
          }
        }
        
        return updatedColumn;
      }
      return column;
    });
    
    setColumns(updatedColumns);
    
    // Validate column name if that's the field being updated
    if (field === 'name') {
      validateColumnName(columnId, value);
    }
  };
  
  // Validate table name
  const validateTableName = (name) => {
    const isValid = name.trim() !== '' && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
    const isDuplicate = existingTables.includes(name);
    setValidation(prev => ({ ...prev, tableName: isValid && !isDuplicate }));
    
    if (!isValid) {
      setError('Table name must start with a letter or underscore and contain only letters, numbers, and underscores');
    } else if (isDuplicate) {
      setError(`Table '${name}' already exists`);
    } else {
      setError('');
    }
    
    return isValid && !isDuplicate;
  };
  
  // Validate column name
  const validateColumnName = (columnId, name) => {
    const isValid = name.trim() !== '' && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
    
    // Check for duplicate column names
    const columnNames = columns.map(col => col.name.toLowerCase());
    const isDuplicate = columnNames.filter(n => n === name.toLowerCase()).length > 1;
    
    const columnValid = isValid && !isDuplicate;
    
    setValidation(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [columnId]: { ...prev.columns[columnId], name: columnValid }
      }
    }));
    
    return columnValid;
  };
  
  // Validate the entire form
  const validateForm = () => {
    const tableNameValid = validateTableName(newTableName);
    
    let columnsValid = true;
    const columnValidation = {};
    
    columns.forEach(column => {
      const isValidName = column.name.trim() !== '' && 
        /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(column.name);
      
      // Check for duplicate column names
      const duplicateCount = columns.filter(
        col => col.name.toLowerCase() === column.name.toLowerCase()
      ).length;
      
      const nameValid = isValidName && duplicateCount === 1;
      columnValidation[column.id] = { name: nameValid };
      
      if (!nameValid) {
        columnsValid = false;
      }
    });
    
    setValidation(prev => ({
      tableName: tableNameValid,
      columns: columnValidation
    }));
    
    // Check if at least one primary key is defined
    const hasPrimaryKey = columns.some(col => col.primaryKey);
    if (!hasPrimaryKey) {
      notification.showWarning('Tables should have at least one primary key column');
      return false;
    }
    
    return tableNameValid && columnsValid;
  };
  
  // Generate SQL for table creation
  const generateCreateTableSQL = () => {
    let sql = `CREATE TABLE "${newTableName}" (\n`;
    
    // Add column definitions
    const columnDefs = columns.map(column => {
      let def = `  "${column.name}" ${column.type}`;
      
      if (column.primaryKey) {
        def += ' PRIMARY KEY';
        
        if (column.autoIncrement && column.type === 'INTEGER') {
          def += ' AUTOINCREMENT';
        }
      }
      
      if (column.notNull) {
        def += ' NOT NULL';
      }
      
      if (column.unique && !column.primaryKey) {
        def += ' UNIQUE';
      }
      
      if (column.default.trim() !== '') {
        def += ` DEFAULT ${column.default}`;
      }
      
      return def;
    });
    
    sql += columnDefs.join(',\n');
    sql += '\n);';
    
    return sql;
  };
  
  // Preview SQL before creating the table
  const handlePreviewSQL = () => {
    if (!validateForm()) return;
    
    const sql = generateCreateTableSQL();
    setPreviewSql(sql);
    setShowPreview(true);
  };
  
  // Create the table
  const createTable = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const sql = generateCreateTableSQL();
      await db.query(sql);
      
      notification.showSuccess(`Table '${newTableName}' created successfully`);
      
      // Reset form after successful creation
      setNewTableName('');
      setColumns([
        { id: generateColumnId(), name: 'id', type: 'INTEGER', primaryKey: true, autoIncrement: true, notNull: true, default: '', unique: false }
      ]);
      setShowPreview(false);
      
      // Notify parent components
      if (onTableCreated) {
        onTableCreated(newTableName);
      }
      
      if (onRefreshTables) {
        onRefreshTables();
      }
    } catch (err) {
      notification.showError(`Failed to create table: ${err.message}`);
      setError(`Failed to create table: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Initiate table deletion flow
  const handleDeleteTableClick = (tableName) => {
    setTableToDelete(tableName);
    setIsDeleteConfirmOpen(true);
  };
  
  // Delete a table
  const deleteTable = async () => {
    if (!tableToDelete) return;
    
    setLoading(true);
    
    try {
      await db.query(`DROP TABLE "${tableToDelete}"`);
      notification.showSuccess(`Table '${tableToDelete}' deleted successfully`);
      
      // Notify parent component to refresh tables list
      if (onRefreshTables) {
        onRefreshTables();
      }
    } catch (err) {
      notification.showError(`Failed to delete table: ${err.message}`);
    } finally {
      setLoading(false);
      setIsDeleteConfirmOpen(false);
      setTableToDelete('');
    }
  };
  
  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Tab navigation for Create/Delete modes */}
      <div className="flex border-b border-gray-200">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            mode === 'create'
              ? 'border-b-2 border-indigo-500 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setMode('create')}
        >
          Create Table
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            mode === 'delete'
              ? 'border-b-2 border-indigo-500 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setMode('delete')}
        >
          Delete Table
        </button>
      </div>

      {/* Error message display */}
      {error && (
        <div className="m-4 bg-red-50 border-l-4 border-red-400 p-4 text-red-700">
          <p>{error}</p>
        </div>
      )}

      {/* Create Table Form */}
      {mode === 'create' && (
        <div className="p-4">
          <div className="space-y-6">
            {/* Table Name */}
            <div>
              <label htmlFor="tableName" className="block text-sm font-medium text-gray-700">
                Table Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="tableName"
                value={newTableName}
                onChange={(e) => {
                  setNewTableName(e.target.value);
                  validateTableName(e.target.value);
                }}
                className={`mt-1 block w-full rounded-md shadow-sm text-sm ${
                  validation.tableName
                    ? 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                    : 'border-red-300 focus:border-red-500 focus:ring-red-500'
                }`}
              />
              {!validation.tableName && (
                <p className="mt-1 text-sm text-red-600">
                  Please enter a valid table name. Names must start with a letter or underscore
                  and contain only letters, numbers, and underscores.
                </p>
              )}
            </div>

            {/* Columns */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Columns <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={addColumn}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Column
                </button>
              </div>

              <div className="mt-1 bg-gray-50 rounded-md border border-gray-200 overflow-hidden">
                {/* Column Headers */}
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-100 border-b border-gray-200 text-xs font-medium text-gray-700 uppercase tracking-wider">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-6">Constraints</div>
                  <div className="col-span-1">Actions</div>
                </div>

                {/* Column List */}
                <div className="divide-y divide-gray-200">
                  {columns.map((column) => (
                    <div key={column.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
                      {/* Column Name */}
                      <div className="col-span-3">
                        <input
                          type="text"
                          value={column.name}
                          onChange={(e) => updateColumn(column.id, 'name', e.target.value)}
                          className={`block w-full rounded-md shadow-sm text-sm ${
                            validation.columns[column.id]?.name !== false
                              ? 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                              : 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          }`}
                          placeholder="Column name"
                        />
                        {validation.columns[column.id]?.name === false && (
                          <p className="mt-1 text-xs text-red-600">
                            Invalid or duplicate name
                          </p>
                        )}
                      </div>

                      {/* Column Type */}
                      <div className="col-span-2">
                        <select
                          value={column.type}
                          onChange={(e) => updateColumn(column.id, 'type', e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        >
                          {dataTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Constraints */}
                      <div className="col-span-6">
                        <div className="flex flex-wrap gap-3">
                          {/* Primary Key */}
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={column.primaryKey}
                              onChange={(e) => updateColumn(column.id, 'primaryKey', e.target.checked)}
                              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                            />
                            <span className="ml-1 text-sm text-gray-700">Primary Key</span>
                          </label>

                          {/* Auto Increment - only for INTEGER primary keys */}
                          {column.primaryKey && column.type === 'INTEGER' && (
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={column.autoIncrement}
                                onChange={(e) => updateColumn(column.id, 'autoIncrement', e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                              />
                              <span className="ml-1 text-sm text-gray-700">Auto Increment</span>
                            </label>
                          )}

                          {/* Not Null */}
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={column.notNull || column.primaryKey}
                              onChange={(e) => updateColumn(column.id, 'notNull', e.target.checked)}
                              disabled={column.primaryKey} // Primary keys are always NOT NULL
                              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                            />
                            <span className="ml-1 text-sm text-gray-700">Not Null</span>
                          </label>

                          {/* Unique - not needed for primary keys */}
                          {!column.primaryKey && (
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={column.unique}
                                onChange={(e) => updateColumn(column.id, 'unique', e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                              />
                              <span className="ml-1 text-sm text-gray-700">Unique</span>
                            </label>
                          )}

                          {/* Default Value */}
                          <div className="flex items-center">
                            <span className="text-sm text-gray-700 mr-1">Default:</span>
                            <input
                              type="text"
                              value={column.default}
                              onChange={(e) => updateColumn(column.id, 'default', e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                              placeholder="Default value"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 text-right">
                        <button
                          type="button"
                          onClick={() => removeColumn(column.id)}
                          className="text-red-600 hover:text-red-800 focus:outline-none"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handlePreviewSQL}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Preview SQL
              </button>
              <button
                type="button"
                onClick={createTable}
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
              >
                {loading ? 'Creating...' : 'Create Table'}
              </button>
            </div>

            {/* SQL Preview Modal */}
            {showPreview && (
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-xl">
                  <div className="bg-gray-100 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">SQL Preview</h3>
                    <button
                      type="button"
                      onClick={() => setShowPreview(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4 max-h-[60vh] overflow-y-auto">
                    <pre className="bg-gray-800 text-gray-200 p-4 rounded-md overflow-x-auto whitespace-pre-wrap">
                      {previewSql}
                    </pre>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowPreview(false)}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={createTable}
                      disabled={loading}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
                    >
                      {loading ? 'Creating...' : 'Create Table'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Table UI */}
      {mode === 'delete' && (
        <div className="p-4">
          <div className="text-sm text-gray-700 mb-4">
            Select a table to delete. <span className="text-red-500 font-semibold">This action cannot be undone!</span>
          </div>

          {existingTables.length === 0 ? (
            <div className="p-5 text-center text-gray-500 bg-gray-50 rounded-md">
              No tables found in database
            </div>
          ) : (
            <div className="overflow-hidden border border-gray-200 rounded-md">
              <ul className="divide-y divide-gray-200">
                {existingTables.map((tableName) => (
                  <li key={tableName} className="px-4 py-3 flex justify-between items-center hover:bg-gray-50">
                    <span className="text-sm font-medium text-gray-700">{tableName}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteTableClick(tableName)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title="Delete Table"
        message={
          <div>
            <p>
              Are you sure you want to delete the table <span className="font-semibold">{tableToDelete}</span>?
            </p>
            <p className="mt-2 text-red-600">
              This action cannot be undone and all data in this table will be permanently lost.
            </p>
          </div>
        }
        confirmText="Delete Table"
        cancelText="Cancel"
        onConfirm={deleteTable}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        confirmButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        type="danger"
      />
    </div>
  );
};

export default TableCreator;